import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Mail, Key, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Footer } from "../components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

export default function ForumGatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get("token");
  const [activeTab, setActiveTab] = useState(null);
  const [email, setEmail] = useState("");
  const [respondentId, setRespondentId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const checkExistingSession = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
      if (res.ok) {
        navigate("/forum", { replace: true });
        return;
      }
    } catch { /* session check - not logged in */ }
    setCheckingSession(false);
  }, [navigate]);

  const redeemMagicLink = useCallback(async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/fan-pass/magic-link/${token}`, {
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Welcome back! Forum access restored.");
        navigate("/forum", { replace: true });
      } else {
        const err = await res.json();
        toast.error(err.detail || "Link expired or invalid.");
        setCheckingSession(false);
      }
    } catch {
      toast.error("Something went wrong.");
      setCheckingSession(false);
    }
  }, [navigate]);

  useEffect(() => {
    document.title = "Join the Forum - Afro Fanthem";
    window.scrollTo(0, 0);

    // If magic link token in URL, redeem it
    if (magicToken) {
      redeemMagicLink(magicToken);
      return;
    }

    // Check if user already has a valid session
    checkExistingSession();
  }, [magicToken, redeemMagicLink, checkExistingSession]);

  const handleClaim = async (e) => {
    e.preventDefault();
    if (!email.trim() && !respondentId.trim()) {
      toast.error("Enter your email or respondent ID");
      return;
    }
    setSubmitting(true);
    try {
      const body = {};
      if (email.trim()) body.email = email.trim();
      if (respondentId.trim()) body.respondent_id = respondentId.trim();

      const res = await fetch(`${API_URL}/api/fan-pass/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });
      const data = await res.json();

      if (data.status === "granted") {
        toast.success("Forum access granted!");
        navigate("/forum", { replace: true });
      } else if (data.status === "magic_link_sent") {
        setMagicLinkSent(true);
        toast.success("Check your email for an access link.");
      } else {
        setMagicLinkSent(true);
      }
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/fan-pass/magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() })
      });
      setMagicLinkSent(true);
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#FBBF24] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 md:h-14 w-auto" />
            </Link>
            <Link to="/" className="text-gray-300 hover:text-white text-sm transition-colors">Home</Link>
          </div>
        </div>
      </nav>

      <main className="pt-28 md:pt-36 pb-16 px-4">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-4" data-testid="forum-gate-headline">
              Join the Afro Fanthem <span className="text-[#FBBF24]">Fan Forum</span>
            </h1>
            <p className="text-gray-300 leading-relaxed">
              Complete the African Football Fan Survey to unlock your Fan Pass. No full profile is needed. Choose a display name, respect the rules, and join the debate.
            </p>
          </div>

          {/* Option 1: New user */}
          <Link
            to="/survey"
            className="block bg-[#FBBF24]/10 border border-[#FBBF24]/30 p-6 mb-4 hover:bg-[#FBBF24]/15 transition-all group"
            data-testid="gate-new-survey"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg mb-1">I'm new — take the survey</h2>
                <p className="text-gray-400 text-sm">Takes a few minutes. Unlocks forum access immediately.</p>
              </div>
              <ArrowRight className="w-6 h-6 text-[#FBBF24] group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>

          {/* Option 2: Already completed */}
          <button
            onClick={() => setActiveTab(activeTab === "claim" ? null : "claim")}
            className="block w-full text-left bg-white/5 border border-white/10 p-6 mb-4 hover:bg-white/[0.07] transition-all group"
            data-testid="gate-already-completed"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg mb-1">I already completed the survey</h2>
                <p className="text-gray-400 text-sm">Recover your forum access with your email or ID.</p>
              </div>
              <Key className="w-6 h-6 text-gray-400 group-hover:text-[#FBBF24] transition-colors" />
            </div>
          </button>

          {/* Claim form */}
          {activeTab === "claim" && (
            <div className="bg-[#141414] border border-white/10 p-6 mb-4" data-testid="claim-form">
              {!magicLinkSent ? (
                <>
                  <h3 className="font-heading text-xl text-white uppercase mb-2">Already completed the survey?</h3>
                  <p className="text-gray-400 text-sm mb-6">
                    You do not need to retake the survey. If we can match your previous response, we'll help you claim your Fan Pass.
                  </p>
                  <form onSubmit={handleClaim} className="space-y-4">
                    <div>
                      <label className="text-gray-300 text-sm mb-1 block">Email used during registration</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-[#FBBF24]/50 focus:outline-none"
                        data-testid="claim-email-input"
                      />
                    </div>
                    <div>
                      <label className="text-gray-300 text-sm mb-1 block">Or your respondent ID (if you have it)</label>
                      <input
                        type="text"
                        value={respondentId}
                        onChange={(e) => setRespondentId(e.target.value)}
                        placeholder="resp_xxxxxxxxxxxx"
                        className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-[#FBBF24]/50 focus:outline-none"
                        data-testid="claim-id-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#F59E0B] transition-all disabled:opacity-50"
                      data-testid="claim-submit-btn"
                    >
                      {submitting ? "Checking..." : "Claim My Fan Pass"}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-4">
                  <Mail className="w-10 h-10 text-[#FBBF24] mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-white uppercase mb-2">Check Your Email</h3>
                  <p className="text-gray-400 text-sm">
                    If we can match those details, we'll send a private Fan Pass link. Check your inbox.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Option 3: Continue on this device (shown if they have a cookie but it expired) */}
          <button
            onClick={() => setActiveTab(activeTab === "return" ? null : "return")}
            className="block w-full text-left bg-white/5 border border-white/10 p-6 mb-4 hover:bg-white/[0.07] transition-all group"
            data-testid="gate-return"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg mb-1">I had access but it expired</h2>
                <p className="text-gray-400 text-sm">Get a sign-in link sent to your email.</p>
              </div>
              <Mail className="w-6 h-6 text-gray-400 group-hover:text-[#FBBF24] transition-colors" />
            </div>
          </button>

          {activeTab === "return" && (
            <div className="bg-[#141414] border border-white/10 p-6 mb-4" data-testid="return-form">
              {!magicLinkSent ? (
                <form onSubmit={handleMagicLinkRequest} className="space-y-4">
                  <p className="text-gray-400 text-sm mb-2">
                    Enter the email you saved with your Fan Pass. We'll send a private sign-in link.
                  </p>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-[#FBBF24]/50 focus:outline-none"
                    data-testid="return-email-input"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#F59E0B] transition-all disabled:opacity-50"
                    data-testid="return-submit-btn"
                  >
                    {submitting ? "Sending..." : "Send Sign-In Link"}
                  </button>
                </form>
              ) : (
                <div className="text-center py-4">
                  <Mail className="w-10 h-10 text-[#FBBF24] mx-auto mb-4" />
                  <p className="text-gray-400 text-sm">
                    If we can match those details, we'll send a private access link.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Forum rules note */}
          <div className="mt-8 flex items-start gap-3 p-4 bg-white/[0.02] border border-white/5">
            <Shield className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <p className="text-gray-500 text-sm">
              By joining the forum you agree to the <Link to="/forum-rules" className="text-[#FBBF24] hover:underline">Forum Rules</Link>. Passionate debate is welcome. Abuse is not.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
